import CustomInput from "components/CustomInput/CustomInput";
import { Field, Form, Formik } from "formik";
import Modal from "react-responsive-modal";
import * as Yup from "yup";
import ButtonWithProgress from "components/ButtonWithProgress";
import AttachmentSelection from "components/AttachmentSelection/AttachmentSelection";
import classNames from "classnames";
import AttachmentPreview from "components/AttachmentPreview/AttachmentPreview";
import GeneralService from "services/GeneralService";
import WellnessService from "services/WellnessService";
import { useToasts } from "react-toast-notifications";
import { useState } from "react";
import { useTeamContext } from "store/contexts/teamContext";
import CustomButton from "components/CustomButton";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import ThumbnailUpload from "components/ThumbnailUpload/ThumbnailUpload";

const classes = {
  errorInput: {
    borderWidth: "2px",
    borderColor: "#FF0000",
  },
};

const UploadModal = ({ activeTab, onClose, setReload }) => {
  const initialValues = { title: "", short_desc: "", attachment: null, link: "", uploadType: "document", thumbnail: null };
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const acceptedMimeTypes =
    activeTab === 3 ? ["application", "text", "video", "audio"] : ["video", "audio"];

  const { team } = useTeamContext();
  const { addToast } = useToasts();

  const attachmentTypes = {
    video: 3,
    audio: 4,
    application: 5,
    text: 5,
  };
  const formSchema = Yup.object().shape({
    title: Yup.string().trim().required("Please add title."),
    short_desc: Yup.string().trim()
    .min(4, "Short description must be at least 4 characters long.")
    .max(250, "Short description should not be greater then 250")
    .required("Please add short description."),
    uploadType: Yup.string().nullable(),
    link: Yup.string().when("uploadType", {
      is: (val) => activeTab === 3 && val === "link",
      then: Yup.string().matches(/^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/i, "Please enter/paste a valid URL/link.").required("Please paste a valid link."),
      otherwise: Yup.string().nullable()
    }),
    attachment: Yup.mixed().when("uploadType", {
      is: (val) => activeTab !== 3 || val === "document",
      then: Yup.mixed().required("Please attach the media"),
      otherwise: Yup.mixed().nullable()
    }),
    thumbnail: Yup.mixed().nullable()
  });

  const submitForm = async (values) => {
    try {
      setSubmitting(true);
      let media = null;
      let attachmentType = null;

      if (values.attachment) {
        const preSignedResponse = await GeneralService.getPresignedUploadURL({
          filename: values.attachment.name,
          mime_type: values.attachment.type,
          file_size: values.attachment.size,
          resource_type: "team",
          resource_id: team.id,
          resource_policy: "team.wellnessVideo",
        });

        const { key, url } = preSignedResponse.data.data;

        media = key;

        await GeneralService.uploadToS3(url, values.attachment, (progress) => {
          setUploadProgress(`${Math.floor(progress)}%`);
        });

        setUploadProgress(100);
        attachmentType = values.attachment.type.split("/");
        setUploadProgress(0);
      } else if (values.link) {
        media = values.link;
        attachmentType = ["link"];
      }

      let thumbnailMedia = null;
      if (activeTab === 3 && values.thumbnail) {
        const thumbnailPreSignedResponse = await GeneralService.getPresignedUploadURL({
          filename: values.thumbnail.name,
          mime_type: values.thumbnail.type,
          file_size: values.thumbnail.size,
          resource_type: "team",
          resource_id: team.id,
          resource_policy: "team.wellnessVideo",
        });

        const { key: thumbKey, url: thumbUrl } = thumbnailPreSignedResponse.data.data;
        thumbnailMedia = thumbKey;

        await GeneralService.uploadToS3(thumbUrl, values.thumbnail, () => {});
      }

      let res;
      if (activeTab === 3) {
        // Training Documents — use dedicated endpoint
        const payload = {
          title: values.title,
          short_desc: values.short_desc,
          team_id: team.id,
          status: 1,
        };
        
        if (values.attachment) {
          payload.url = media;
          payload.media_type_id = attachmentTypes[attachmentType[0]];
          payload.media_type = attachmentType[0];
        } else if (values.link) {
          payload.link = /^(https?:\/\/)/i.test(values.link) ? values.link : `https://${values.link}`;
        }

        if (thumbnailMedia) {
          payload.thumbnail = thumbnailMedia;
        }

        res = await WellnessService.createTrainingDocument(team.id, payload);
        addToast("Your document is uploaded.", { appearance: "success" });
      } else {
        const payload = {
          url: media,
          title: values.title,
          short_desc: values.short_desc,
          team_id: team.id,
          type: 3,
          media_type_id: attachmentTypes[attachmentType[0]],
          media_type: attachmentType[0],
          status: 1,
        };
        res = await WellnessService.createWellnessVideo(payload);
        addToast("Your video is uploaded.", { appearance: "success" });
      }

      setReload(res.data);
      onClose(false);
      setSubmitting(false);
    } catch (e) {
      setSubmitting(false);
      console.log(e);
    }
  };

  return (
    <Modal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      classNames={{
        modalContainer: "modalContainer",
        modal: "createTaskModal",
      }}
      open
      showCloseIcon={false}
      onClose={() => onClose(false)}
    >
      <div className="modal">
        <h5 className="text-center createTaskHeading">
          {activeTab === 3 ? "Add Training Document" : "Add Training Media"}
        </h5>
        <CustomButton type="button" buttonClass="btnClose">
          <CloseIcon style={{ width: "20px" }} onClick={() => onClose(false)} />
        </CustomButton>
        <div className="">
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            onSubmit={(values) => {
              submitForm(values);
            }}
          >
            <Form>
              <Field name="title">
                {({ field, form, meta }) => (
                  <>
                    <CustomInput
                      inputProps={{
                        placeholder: "Title",
                        value: field.title,
                        name: "title",
                        error:
                          !!form.errors.title && !!form.touched.title
                            ? form.errors.title
                            : null,
                      }}
                      inputClass="create-text-padding uploadModalInput"
                      onChange={field.onChange}
                    />
                  </>
                )}
              </Field>
              <Field name="short_desc">
                {({ field, form, meta }) => (
                  <>
                    <div className="d-flex gap-2">
                      <textarea
                        placeholder="Short Description"
                        value={field.short_desc}
                        name="short_desc"
                        onChange={field.onChange}
                        rows={5}
                        className={classNames({
                          "user-profile-inputs": true,
                          "create-text-padding": true,
                          uploadModalTextArea: true,
                        })}
                        style={
                          !!form.errors.short_desc && !!form.touched.short_desc
                            ? classes.errorInput
                            : null
                        }
                      />
                      {!!form.values.attachment && (
                        <AttachmentPreview
                          attachment={form.values.attachment}
                          clearAttachment={() =>
                            form.setFieldValue("attachment", null)
                          }
                        />
                      )}
                    </div>
                    {form?.errors?.short_desc && (
                      <>
                        <span className="errorMessage">
                          {form?.errors?.short_desc}
                        </span>
                      </>
                    )}
                    {form?.errors?.attachment && (
                      <div className="mt-2">
                        <span className="errorMessage">
                          {form?.errors?.attachment}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </Field>
              {activeTab === 3 && (
                <Field name="uploadType">
                  {({ field, form }) => (
                    <div className="d-flex gap-4 mt-3 mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="uploadType"
                          id="uploadDocument"
                          value="document"
                          checked={field.value === "document"}
                          onChange={() => {
                            form.setFieldValue("uploadType", "document");
                            form.setFieldValue("link", "");
                          }}
                        />
                        <label className="form-check-label" htmlFor="uploadDocument" style={{cursor: "pointer"}}>
                          Upload Document
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="uploadType"
                          id="pasteLink"
                          value="link"
                          checked={field.value === "link"}
                          onChange={() => {
                            form.setFieldValue("uploadType", "link");
                            form.setFieldValue("attachment", null);
                          }}
                        />
                        <label className="form-check-label" htmlFor="pasteLink" style={{cursor: "pointer"}}>
                          Paste Link
                        </label>
                      </div>
                    </div>
                  )}
                </Field>
              )}
              <Field name="link">
                {({ field, form }) => (
                  activeTab === 3 && form.values.uploadType === "link" ? (
                    <div className="mt-3">
                      <CustomInput
                        inputProps={{
                          placeholder: "Paste URL / Link here",
                          value: field.value,
                          name: "link",
                          error: !!form.errors.link && !!form.touched.link ? form.errors.link : null,
                        }}
                        inputClass="create-text-padding uploadModalInput"
                        onChange={field.onChange}
                      />
                    </div>
                  ) : null
                )}
              </Field>
              <Field name="attachment">
                {({ field, form, meta }) => (
                  (activeTab !== 3 || form.values.uploadType === "document") ? (
                    <div className="riddleFooterButton d-flex mt-3">
                      <div className="writepost-footer">
                        <div className="writepost-left-btns" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <AttachmentSelection
                            attachmentText={activeTab === 3 ? (form.values.attachment ? "Change Document" : "Upload Document") : null}
                            onChange={(file) => {
                              if (
                                acceptedMimeTypes.includes(
                                  file.type.split("/")[0]
                                ) || activeTab === 3 // in case the doc doesn't match standard prefixes, but allowed below
                              ) {
                                form.setFieldValue("attachment", file);
                              }
                            }}
                            accepted={
                              activeTab === 3
                                ? [".mp4", ".webm", ".pdf", ".docx", ".doc", ".xlsx", ".xls", ".pptx", ".ppt", ".txt"]
                                : [".mp4", ".webm"]
                            }
                            selectors={
                              activeTab === 3
                                ? ["video", "screen", "image"]
                                : ["video", "screen"]
                            }
                            allowDocuments={activeTab === 3}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null
                )}
              </Field>
              {activeTab === 3 && (
                <Field name="thumbnail">
                  {({ field, form }) => (
                    <div className="mt-4">
                      <label className="form-label" style={{ fontWeight: 600 }}>Thumbnail (Optional)</label>
                      <ThumbnailUpload
                        thumbnail={form.values.thumbnail}
                        onThumbnailChange={(file) => form.setFieldValue("thumbnail", file)}
                        onThumbnailRemove={() => form.setFieldValue("thumbnail", null)}
                      />
                    </div>
                  )}
                </Field>
              )}
              <div>
                <ButtonWithProgress
                  initialText={"Submit"}
                  isSubmitting={submitting}
                  buttonWithProgressClass="btn grad_btn full_w mt-3"
                  progress={uploadProgress}
                />
              </div>
            </Form>
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export default UploadModal;
